/**
 * Reporte de cumplimiento, exportable a PDF -- a pedido del DPD: "poder sacar
 * reportes... de las unidades totales o algún reporte de cumplimiento de la
 * normativa". No hay backend nuevo: reusa fetchInstitutionalDashboard/
 * fetchUnitDashboard/fetchActivities (ya construidos para los tableros) y
 * los presenta en una vista pensada para imprimirse -- `window.print()` +
 * el CSS `@media print` que ya traía el proyecto (styles.css, nunca antes
 * usado por ninguna pantalla) oculta automáticamente el sidebar/topbar/
 * botones (`.no-print`) sin tocar AppShell.
 *
 * Dos alcances, misma plantilla:
 *  - /reportes            -- institucional: agregado por unidad (para no
 *    imprimir cientos de filas de actividad en un solo documento).
 *  - /reportes/unidad/:id -- una unidad: detalle actividad por actividad,
 *    con las mismas banderas de riesgo que ya expone /activities.
 */
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { can, isMemberOf, ACTIVITY_STATUS_LABEL, type ActivityStatus, type UnitRatStatus } from '@rat/shared';
import {
  fetchActivities,
  fetchInstitutionalDashboard,
  fetchUnitDashboard,
  type ActivityRow,
} from '../lib/queries';
import { useAuth } from '../auth/AuthProvider';
import { QueryState, UnitStatusPill } from '../components/ui';
import { Wordmark } from '../components/Logo';
import { AttentionTable, RiskKpis } from '../components/DashboardWidgets';
import { Unauthorized } from './Misc';

function generatedFooter(email?: string) {
  const now = new Date();
  return `Generado automáticamente por el sistema RAT el ${now.toLocaleDateString('es-CL')} a las ${now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}${email ? ` por ${email}` : ''}.`;
}

export function Report() {
  const { unitId } = useParams();
  const { authz, session } = useAuth();

  const canSeeInstitutional = can(authz, 'institutional.view') || can(authz, 'unit.read.all');
  const canSeeThisUnit = unitId ? isMemberOf(authz, unitId) || canSeeInstitutional : false;
  if (unitId ? !canSeeThisUnit : !canSeeInstitutional) return <Unauthorized />;

  return unitId ? <UnitReport unitId={unitId} email={session?.user.email} /> : <InstitutionalReport email={session?.user.email} />;
}

function ReportHeader({ scope }: { scope: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4 }}>
      <Wordmark size={34} />
      <div>
        <div style={{ fontFamily: 'var(--f-display)', fontWeight: 700, fontSize: 17 }}>
          Reporte de cumplimiento — Registro de Actividades de Tratamiento
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          Ley 21.719 sobre Protección de Datos Personales · Alcance: {scope}
        </div>
      </div>
    </div>
  );
}

function ReportFooter({ email }: { email?: string }) {
  return (
    <p className="muted" style={{ fontSize: 10.5, marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      {generatedFooter(email)}
    </p>
  );
}

function PrintBar() {
  return (
    <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
      <button className="btn btn--primary" onClick={() => window.print()}>
        Imprimir / exportar PDF
      </button>
    </div>
  );
}

function InstitutionalReport({ email }: { email?: string }) {
  const q = useQuery({ queryKey: ['dash', 'inst'], queryFn: fetchInstitutionalDashboard });

  return (
    <div className="printable">
      <PrintBar />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <>
            <ReportHeader scope="institucional (todas las unidades)" />

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Resumen</h3>
              <div className="kpis" style={{ marginTop: 8 }}>
                <div className="kpi">
                  <div className="n">{q.data.unidades_total}</div>
                  <div className="l">Unidades</div>
                </div>
                <div className="kpi">
                  <div className="n">{q.data.actividades_total}</div>
                  <div className="l">Actividades</div>
                </div>
                {(Object.keys(q.data.por_estado) as ActivityStatus[]).map((s) => (
                  <div className="kpi" key={s}>
                    <div className="n">{q.data!.por_estado[s]}</div>
                    <div className="l">{ACTIVITY_STATUS_LABEL[s]}</div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Riesgo y cumplimiento</h3>
              <RiskKpis riesgo={q.data.riesgo} />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Necesitan atención (Observado hace +14 días)</h3>
              <AttentionTable rows={q.data.atencion} showUnit />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Avance por unidad</h3>
              <div className="tablewrap" style={{ border: 0, marginTop: 8 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Unidad</th>
                      <th>Estado</th>
                      <th>Actividades</th>
                      <th>Completitud</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.por_unidad.map((u) => (
                      <tr key={u.unit_id}>
                        <td>{u.name}</td>
                        <td>
                          <UnitStatusPill status={u.rat_status as UnitRatStatus} />
                        </td>
                        <td className="mono">{u.activities_total}</td>
                        <td className="mono">{u.completeness_avg}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <ReportFooter email={email} />
          </>
        )}
      </QueryState>
    </div>
  );
}

function UnitReport({ unitId, email }: { unitId: string; email?: string }) {
  const dash = useQuery({ queryKey: ['dash', 'unit', unitId], queryFn: () => fetchUnitDashboard(unitId) });
  const acts = useQuery({ queryKey: ['activities', 'unit', unitId], queryFn: () => fetchActivities({ unitId, limit: 200 }) });

  return (
    <div className="printable">
      <PrintBar />
      <QueryState isLoading={dash.isLoading} error={dash.error}>
        {dash.data && (
          <>
            <ReportHeader scope={dash.data.unit_name} />

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Resumen</h3>
              <div className="kpis" style={{ marginTop: 8 }}>
                <div className="kpi">
                  <div className="n">{dash.data.activities_total}</div>
                  <div className="l">Actividades</div>
                </div>
                <div className="kpi">
                  <div className="n">{dash.data.completeness_avg}%</div>
                  <div className="l">Completitud promedio</div>
                </div>
                <div className="kpi">
                  <div className="n">{dash.data.observaciones_abiertas}</div>
                  <div className="l">Observaciones abiertas</div>
                </div>
                {(Object.keys(dash.data.by_status) as ActivityStatus[]).map((s) => (
                  <div className="kpi" key={s}>
                    <div className="n">{dash.data!.by_status[s]}</div>
                    <div className="l">{ACTIVITY_STATUS_LABEL[s]}</div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Riesgo y cumplimiento</h3>
              <RiskKpis riesgo={dash.data.riesgo} />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Necesitan atención (Observado hace +14 días)</h3>
              <AttentionTable rows={dash.data.atencion} />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Detalle de actividades</h3>
              <QueryState isLoading={acts.isLoading} error={acts.error}>
                <div className="tablewrap" style={{ border: 0, marginTop: 8 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Actividad</th>
                        <th>Estado</th>
                        <th>Completitud</th>
                        <th>Banderas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(acts.data ?? []).map((a: ActivityRow) => (
                        <tr key={a.id}>
                          <td className="mono">{a.ref_code}</td>
                          <td>{a.title || '(sin nombre)'}</td>
                          <td>{ACTIVITY_STATUS_LABEL[a.status]}</td>
                          <td className="mono">{a.completeness_pct}%</td>
                          <td style={{ fontSize: 11 }}>
                            {[
                              a.has_sensitive_data && 'Datos sensibles',
                              a.has_international_transfer && 'Transf. internacional',
                              a.has_automated_decision && 'Decisión automatizada',
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </td>
                        </tr>
                      ))}
                      {acts.data?.length === 0 && (
                        <tr>
                          <td colSpan={5} className="muted" style={{ padding: 16 }}>
                            Esta unidad aún no registra actividades de tratamiento.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </QueryState>
            </section>

            <ReportFooter email={email} />
          </>
        )}
      </QueryState>
    </div>
  );
}
